import { useState } from "react";
import { Play, Youtube, ArrowUpRight } from "lucide-react";
import SEO from "../seo/SEO";
import {
  physicianSchema,
  medicalClinicSchemas,
  webPageSchema,
  breadcrumbSchema,
} from "../seo/schemas";
import { VIDEOS, YOUTUBE_CHANNEL } from "../data/site";

export default function Videos() {
  const [activeCat, setActiveCat] = useState("All");
  const cats = ["All", ...VIDEOS.categories];
  const filtered =
    activeCat === "All"
      ? VIDEOS.all
      : VIDEOS.all.filter((v) => v.category === activeCat);

  return (
    <main data-testid="videos-page">
      <SEO
        title="Patient Videos — Dr. Indresh Dixit, Gastroenterologist, Varanasi"
        description="YouTube video knowledge center by Dr. Indresh Dixit (DM Gastro, BHU). Patient education on endoscopy, fatty liver, hepatitis, IBS, acidity and diet — all in Hindi & English."
        canonical="/videos"
        schemas={[
          physicianSchema(),
          ...medicalClinicSchemas(),
          webPageSchema({
            name: "Videos — Dr. Indresh Dixit",
            description: "Patient education videos from Dr. Indresh Dixit's YouTube channel.",
            url: "/videos",
          }),
          breadcrumbSchema([
            { label: "Home", url: "/" },
            { label: "Videos" },
          ]),
        ]}
      />

      <section className="border-b border-black/10">
        <div className="container-x py-14 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div className="overline">Video Knowledge Center</div>
            <h1
              data-testid="videos-h1"
              className="font-serif-display mt-5 text-5xl sm:text-6xl lg:text-[72px] leading-[1.02] tracking-tight"
            >
              Watch first.{" "}
              <span className="text-[#0370AD]">Then decide.</span>
            </h1>
            <p className="mt-7 text-black/70 max-w-2xl">
              Honest, doctor-led patient education from{" "}
              <strong>Dr. Indresh Dixit</strong> — in Hindi and English. Pick a
              topic, watch, ask questions on WhatsApp.
            </p>
          </div>
          <div className="lg:col-span-4 lg:flex lg:items-end lg:justify-end">
            <a
              href={YOUTUBE_CHANNEL}
              target="_blank"
              rel="noreferrer"
              data-testid="videos-subscribe-btn"
              className="group inline-flex items-center gap-2 bg-[#FF0000] text-white px-5 py-3 rounded-full font-semibold text-[14px] hover:bg-[#cc0000] transition-colors"
            >
              <Youtube className="h-4 w-4" />
              Subscribe on YouTube
              <ArrowUpRight className="h-4 w-4 arrow" />
            </a>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="border-b border-black/10 bg-[#fbfaf7]">
        <div className="container-x py-6 overflow-x-auto">
          <div className="flex gap-2.5 w-max">
            {cats.map((c) => (
              <button
                key={c}
                onClick={() => setActiveCat(c)}
                data-testid={`videos-cat-${c.replace(/\s/g, "-").toLowerCase()}`}
                className={`px-4 py-2 rounded-full border text-[13px] font-medium transition-colors ${
                  activeCat === c
                    ? "bg-black text-white border-black"
                    : "border-black/15 hover:bg-black hover:text-white"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Grid of videos with embedded iframes */}
      <section className="section-pad">
        <div className="container-x">
          <h2 className="font-serif-display text-3xl md:text-4xl tracking-tight mb-10">
            All videos from the channel.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((v, i) => (
              <VideoCard key={v.id + i} video={v} />
            ))}
          </div>
          {filtered.length === 0 && (
            <p className="text-black/60">No videos in this category yet.</p>
          )}
        </div>
      </section>
    </main>
  );
}

function VideoCard({ video }) {
  const [playing, setPlaying] = useState(false);
  return (
    <div
      data-testid={`video-card-${video.id}`}
      className="bg-white border border-black/10 rounded-[6px] overflow-hidden hover:border-[#0370AD] transition-colors"
    >
      <div className="aspect-video bg-black relative">
        {playing ? (
          <iframe
            title={video.title}
            src={`https://www.youtube.com/embed/${video.id}?autoplay=1&rel=0`}
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            data-testid={`video-play-${video.id}`}
            className="group absolute inset-0 w-full h-full"
            aria-label={`Play ${video.title}`}
          >
            <img
              src={`https://img.youtube.com/vi/${video.id}/hqdefault.jpg`}
              alt={video.title}
              className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-black/15 group-hover:bg-black/25 transition-colors" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-14 w-14 rounded-full bg-white/95 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Play className="h-5 w-5 ml-1 text-[#0370AD]" />
              </div>
            </div>
          </button>
        )}
      </div>
      <div className="p-5">
        <div className="text-[11px] tracking-[0.18em] uppercase text-[#0370AD] font-semibold">
          {video.category} · {video.duration}
        </div>
        <h3 className="mt-2 font-display-semibold text-[18px] leading-snug">
          {video.title}
        </h3>
      </div>
    </div>
  );
}
